import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface User {
    Id: string;
    Email: string | null;
    Username: string | null;
    ClientId: string | null;
    IsServiceAccount: boolean;
    Tenants: string[];
    PermissionIdsByTenant: Record<string, string>;
    IsActive: boolean;
}

export interface ServiceAccountCredentials {
    ClientId: string;
    ClientSecret: string;
    TokenEndpoint: string;
}

export interface CreateServiceAccountRequest {
    Username: string;
    IsServiceAccount?: boolean;
    Tenants: string[];
    PermissionIdsByTenant: Record<string, string>;
    IsActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private api: AxiosInstance;

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private authService: KeycloakAuthService, private apiConfig: ApiConfigService) {
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to dynamically set baseURL and include OAuth token
    this.api.interceptors.request.use((config) => {
      // Dynamically construct baseURL on each request to get current tenantId
      if (isPlatformBrowser(this.platformId)) {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        config.baseURL = `${baseUrl}/user`;
      }

      const token = this.authService.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

    async getUser(userId: string): Promise<User | null> {
        try {
            const response = await this.api.get(`/${userId}`);
            return response.data;
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    }

    async getPermissionsOfUser(userId: string): Promise<Record<string, string[]>> {
        try {
            const response = await this.api.get(`/${userId}/permissions`);
            return response.data;
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    }

    async getAllUsers(): Promise<User[] | null> {
        try {
            const response = await this.api.get('/');
            return response.data;
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    }

    async createUser(request: CreateServiceAccountRequest): Promise<ServiceAccountCredentials> {
        try {
            // ACIT-534: send a pre-serialized JSON string. axios deep-merges object bodies via mergeConfig and silently drops
            // keys named 'prototype'/'constructor'/'__proto__' (e.g. a tenant called 'prototype' in PermissionIdsByTenant).
            const response = await this.api.post('/', JSON.stringify(request));
            return response.data;
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    }

    async updateUser(user: User): Promise<AxiosResponse | null> {
        try {
            // ACIT-534: send a pre-serialized JSON string. axios deep-merges object bodies via mergeConfig and silently drops
            // keys named 'prototype'/'constructor'/'__proto__' (e.g. a tenant called 'prototype' in PermissionIdsByTenant).
            const response = await this.api.put(`/${user.Id}`, JSON.stringify(user));
            return response;
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    }
    
    async deleteUser(userId: string): Promise<AxiosResponse | null> {
        try {
            const response = await this.api.delete(`/${userId}`);
            return response;
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    }
}
    
